import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import MonitorCard from 'components/Dashboard/MonitorCard';
import StatCards from 'components/Dashboard/StatCards';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import { useTableData } from 'hooks/useTableData';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';

// GET request for picking summary data
const getPickingSummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/picking-summary?filterDate=${new Date(filterDate).toISOString()}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching picking summary data:', error);
    throw error;
  }
};

const themeColorMap = shade => {
  return {
    700: 'workspace-stat-card--accent',
    600: 'workspace-stat-card--accent',
    500: 'workspace-stat-card--accent-soft',
    400: 'workspace-stat-card--accent-soft'
  }[shade];
};

const DailyPickSummary = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasFetched = useRef(false);

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd');

  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(currentDateForPicker);

  // State for API data
  const [pickingSummaryData, setPickingSummaryData] = useState({
    dailyPickingSummary: {
      totalNetPicked: 0,
      pickerCount: 0,
      averagePerWorker: 0,
      averageUnitsPerHour: 0
    },
    dailyPickingSummarySameHourPreviousDay: {
      totalNetPicked: 0,
      pickerCount: 0,
      averagePerWorker: 0,
      averageUnitsPerHour: 0
    },
    pickerGroupsMonitor: [],
    pickersMonitor: [],
    blockMonitor: [],
    loading: false,
    error: null
  });

  const fetchPickingSummaryData = async (dateToFetch = selectedDate) => {
    if (!user?.farmId) return;

    try {
      setPickingSummaryData(prev => ({ ...prev, loading: true, error: null }));

      const data = await getPickingSummaryData(user.farmId, dateToFetch);

      setPickingSummaryData(prev => ({
        ...prev,
        dailyPickingSummary: data.dailyPickingSummary || prev.dailyPickingSummary,
        dailyPickingSummarySameHourPreviousDay:
          data.dailyPickingSummarySameHourPreviousDay ||
          prev.dailyPickingSummarySameHourPreviousDay,
        pickerGroupsMonitor: data.pickerGroupsMonitor || [],
        pickersMonitor: data.pickersMonitor || [],
        blockMonitor: data.blockMonitor || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching picking summary:', error);
      setPickingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load picking summary data'
      }));
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchPickingSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.farmId]);

  // Refetch when selectedDate changes
  useEffect(() => {
    if (!hasFetched.current) return;
    fetchPickingSummaryData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPickingSummary, dailyPickingSummarySameHourPreviousDay } = pickingSummaryData;
    const selectedDateFormatted = DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy');

    return [
      {
        title: t('dashboard.totalNetPicked'),
        value: dailyPickingSummary.totalNetPicked?.toLocaleString() || '0',
        unit: t('dashboard.units'),
        sub: `${t('dashboard.lastPickingDay')} (${selectedDateFormatted})`,
        color: themeColorMap(700)
      },
      {
        title: t('dashboard.totalNetPicked'),
        value: dailyPickingSummarySameHourPreviousDay.totalNetPicked?.toLocaleString() || '0',
        unit: t('dashboard.units'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.pickersCount'),
        value: dailyPickingSummary.pickerCount?.toString() || '0',
        unit: t('dashboard.workers'),
        sub: `${t('dashboard.lastPickingDay')} (${selectedDateFormatted})`,
        color: themeColorMap(700)
      },
      {
        title: t('dashboard.pickersCount'),
        value: dailyPickingSummarySameHourPreviousDay.pickerCount?.toString() || '0',
        unit: t('dashboard.workers'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(500)
      },
      {
        title: t('dashboard.avgPerPicker'),
        value: dailyPickingSummary.averagePerWorker?.toFixed(2) || '0',
        unit: t('dashboard.units'),
        sub: `${t('dashboard.lastPickingDay')} (${selectedDateFormatted})`,
        color: themeColorMap(600)
      },
      {
        title: t('dashboard.avgPerPicker'),
        value: dailyPickingSummarySameHourPreviousDay.averagePerWorker?.toFixed(2) || '0',
        unit: t('dashboard.units'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(400)
      },
      {
        title: t('dashboard.workerAvg'),
        value: dailyPickingSummary.averageUnitsPerHour?.toFixed(2) || '0',
        unit: t('dashboard.unitsPerHour'),
        sub: `${t('dashboard.lastPickingDay')} (${selectedDateFormatted})`,
        color: themeColorMap(700)
      },
      {
        title: t('dashboard.workerAvg'),
        value: dailyPickingSummarySameHourPreviousDay.averageUnitsPerHour?.toFixed(2) || '0',
        unit: t('dashboard.unitsPerHour'),
        sub: t('dashboard.sameHourPrevious'),
        color: themeColorMap(500)
      }
    ];
  };

  // Use the custom hook to generate table data with totals
  const pickerGroupsTableData = useTableData(
    pickingSummaryData.pickerGroupsMonitor,
    'pickerGroups'
  );
  const pickerMonitorTableData = useTableData(pickingSummaryData.pickersMonitor, 'pickerMonitor');
  const blockMonitorTableData = useTableData(pickingSummaryData.blockMonitor, 'blockMonitor');

  if (pickingSummaryData.loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="flex justify-center items-center h-64">
          <div className="workspace-summary-message text-lg">
            {t('dashboard.loadingPickingSummary')}
          </div>
        </div>
      </div>
    );
  }

  if (pickingSummaryData.error) {
    return (
      <div className="min-h-screen p-6">
        <div className="flex justify-center items-center h-64">
          <div className="workspace-summary-error text-lg">{pickingSummaryData.error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <SectionIntro
        kicker={t('pageIntro.dashboardKicker')}
        title={t('dashboard.dailyPickSummary')}
        description={t('pageIntro.dailyPickSummaryDescription')}
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="pick-date" className="workspace-summary-date-label text-sm">
              {t('dashboard.selectDate')}
            </label>
            <input
              id="pick-date"
              type="date"
              className="workspace-summary-date-input rounded border px-2 py-1 text-sm"
              value={selectedDate}
              max={currentDateForPicker}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
        }
        className="mb-4"
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <StatCards cards={createCardsData()} />

      <div className="grid gap-4 mt-4">
        <MonitorCard
          title={t('dashboard.pickerGroupsMonitor')}
          date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')}
          hasData={pickerGroupsTableData.rows.length > 0}
          color="green"
        >
          <TableComponent
            tableData={pickerGroupsTableData}
            defaultSortColumn={pickerGroupsTableData.columns[0]?.key}
          />
        </MonitorCard>

        <MonitorCard
          title={t('dashboard.pickerMonitor')}
          date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')}
          hasData={pickerMonitorTableData.rows.length > 0}
          color="green"
        >
          <TableComponent
            tableData={pickerMonitorTableData}
            defaultSortColumn={pickerMonitorTableData.columns[0]?.key}
          />
        </MonitorCard>

        <MonitorCard
          title={t('dashboard.blockMonitor')}
          date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')}
          hasData={blockMonitorTableData.rows.length > 0}
          color="green"
        >
          <TableComponent
            tableData={blockMonitorTableData}
            defaultSortColumn={blockMonitorTableData.columns[0]?.key}
          />
        </MonitorCard>
      </div>
    </div>
  );
};

export default DailyPickSummary;
